import { useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { UploadCloud, Loader2, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Admin image-upload widget. Renders a drag-and-drop zone (or current preview)
 * and posts the file to /api/admin/upload-image. On success it calls
 * `onChange(url)` with the public URL so the parent form can persist it.
 */
const ImageUploader = ({ value, onChange, token, testIdPrefix = 'artist-image' }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Resolve a value (could be absolute http(s) URL or a /api/files/... relative path) into a renderable src
  const resolveSrc = (val) => {
    if (!val) return '';
    if (val.startsWith('http://') || val.startsWith('https://')) return val;
    return `${process.env.REACT_APP_BACKEND_URL}${val}`;
  };

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!ALLOWED.includes(file.type)) {
        toast.error('Please choose a JPG, PNG, WebP or GIF');
        return;
      }
      if (file.size > MAX_BYTES) {
        toast.error('Image must be under 5MB');
        return;
      }
      setUploading(true);
      setProgress(0);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/admin/upload-image`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        onChange(res.data.url);
        toast.success('Image uploaded');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Upload failed');
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [token, onChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const onChangeInput = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so re-picking the same file fires onChange again
    e.target.value = '';
  };

  const clear = () => onChange('');

  return (
    <div data-testid={`${testIdPrefix}-uploader`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={onChangeInput}
        data-testid={`${testIdPrefix}-file-input`}
      />

      {value && !uploading ? (
        // Preview state
        <div className="relative border border-zinc-800 group">
          <img
            src={resolveSrc(value)}
            alt="Artist preview"
            className="w-full h-56 object-cover img-grayscale"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            data-testid={`${testIdPrefix}-preview`}
          />
          <div className="absolute inset-0 bg-[#050505]/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-4 py-2 bg-[#B5FF00] text-[#050505] font-mono text-xs tracking-widest hover:bg-white transition-colors"
              data-testid={`${testIdPrefix}-replace`}
            >
              REPLACE
            </button>
            <button
              type="button"
              onClick={clear}
              className="p-2 border border-zinc-700 text-white hover:border-red-500 hover:text-red-500 transition-colors"
              aria-label="Remove image"
              data-testid={`${testIdPrefix}-remove`}
            >
              <X size={18} />
            </button>
          </div>
        </div>
      ) : (
        // Drop zone state
        <button
          type="button"
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          disabled={uploading}
          className={`w-full h-56 border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-all ${
            dragging
              ? 'border-[#B5FF00] bg-[#B5FF00]/10'
              : 'border-zinc-700 bg-[#050505] hover:border-[#B5FF00]/50 hover:bg-[#B5FF00]/5'
          } ${uploading ? 'cursor-wait' : 'cursor-pointer'}`}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          {uploading ? (
            <>
              <Loader2 size={32} className="animate-spin text-[#B5FF00]" />
              <span className="font-mono text-xs text-zinc-300 tracking-widest">UPLOADING {progress}%</span>
              <div className="w-2/3 h-1 bg-zinc-800">
                <div className="h-full bg-[#B5FF00] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-[#B5FF00]/10 flex items-center justify-center">
                <UploadCloud size={22} className="text-[#B5FF00]" strokeWidth={1.5} />
              </div>
              <div className="text-center">
                <p className="font-mono text-sm text-white tracking-wider">
                  <span className="text-[#B5FF00]">DRAG & DROP</span> OR CLICK TO BROWSE
                </p>
                <p className="font-mono text-xs text-zinc-500 mt-1 tracking-wider">
                  JPG · PNG · WEBP · GIF · MAX 5MB
                </p>
              </div>
            </>
          )}
        </button>
      )}

      {/* Power-user fallback: paste an external URL */}
      <details className="mt-3" data-testid={`${testIdPrefix}-url-fallback`}>
        <summary className="font-mono text-xs text-zinc-500 tracking-wider cursor-pointer hover:text-zinc-300 inline-flex items-center gap-1">
          <ImageIcon size={12} /> USE AN EXTERNAL URL INSTEAD
        </summary>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
          placeholder="https://... or paste an uploaded path"
          data-testid={`${testIdPrefix}-url-input`}
        />
      </details>
    </div>
  );
};

export default ImageUploader;
